import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Feed {
  id: string
  name: string
  provider: string
  type: string
  status: string
  indicators_count: number
  last_updated: string
}

interface Indicator {
  id: string
  type: string
  value: string
  threat_type: string
  severity: string
  confidence: number
  source: string
}

interface Statistics {
  total_feeds: number
  active_feeds: number
  total_indicators: number
  new_indicators_24h: number
  critical_threats: number
  threat_actors_tracked: number
}

export default function ThreatIntelligencePage() {
  const [feeds, setFeeds] = useState<Feed[]>([])
  const [indicators, setIndicators] = useState<Indicator[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'feeds' | 'indicators'>('feeds')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [feedsRes, indicatorsRes, statsRes] = await Promise.all([
        fetch('/api/threatintel/feeds'),
        fetch('/api/threatintel/indicators'),
        fetch('/api/threatintel/statistics')
      ])

      if (feedsRes.ok) setFeeds(await feedsRes.json())
      if (indicatorsRes.ok) setIndicators(await indicatorsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching threat intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSeverityBadgeClass = (severity: string) => {
    const classes: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800',
      informational: 'bg-gray-100 text-gray-800'
    }
    return classes[severity.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Threat Intelligence</h1>
          <p className="mt-2 text-gray-600">Monitor threat feeds and indicators of compromise</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Feeds</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_feeds}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Feeds</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_feeds}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total IOCs</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_indicators.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">New 24h</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.new_indicators_24h}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Critical Threats</p>
              <p className="text-2xl font-bold text-red-600">{statistics.critical_threats}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Actors Tracked</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.threat_actors_tracked}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('feeds')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'feeds'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Feeds ({feeds.length})
            </button>
            <button
              onClick={() => setActiveTab('indicators')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'indicators'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Indicators ({indicators.length})
            </button>
          </nav>
        </div>

        {/* Feeds Grid */}
        {activeTab === 'feeds' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {feeds.map((feed) => (
              <div key={feed.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{feed.name}</h3>
                    <p className="text-sm text-gray-500">{feed.provider}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(feed.status)}`}>
                    {feed.status}
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-sm"><span className="text-gray-500">Type:</span> {feed.type}</p>
                  <p className="text-sm"><span className="text-gray-500">IOCs:</span> {feed.indicators_count.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Updated: {new Date(feed.last_updated).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Indicators Table */}
        {activeTab === 'indicators' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Threat</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {indicators.slice(0, 20).map((indicator) => (
                  <tr key={indicator.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-gray-100 rounded">{indicator.type}</span>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-900 truncate max-w-xs">
                      {indicator.value}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {indicator.threat_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityBadgeClass(indicator.severity)}`}>
                        {indicator.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {Math.round(indicator.confidence * 100)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {indicator.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
